import { of, EMPTY, merge } from 'rxjs';
import { catchError, mergeMap, filter, take } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import get from 'lodash.get';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { emptyArray } from 'ui-components/default-props/empty';
import Global from '../../store/global-selectors';
import * as ReturnGridActions from '../../store/returns/grid/returns.grid.actions';
import * as ReturnActions from '../../store/returns/returns.actions';
import * as ShipmentGridHeaderActions from '../../store/shipments/grid/shipments.grid.header.actions';
import * as ShipmentActions from '../../store/shipments/shipments.actions';
import * as Api from './refresh-tracking.api';
import * as Actions from './refresh-tracking.actions';
import { messages } from './messages';

const PROCESSING_NOTIFICATION_IDS = {};

const showProcessingNotification = (jobGuid) => {
  const refreshProcessingProps = {
    title: <FormattedMessage {...messages.refreshProcessingTitle} />,
    content: <FormattedMessage {...messages.refreshProcessingContent} />,
    notificationType: 'info',
    duration: 2000,
  };
  PROCESSING_NOTIFICATION_IDS[jobGuid] = NotificationService.showNotification({
    ...refreshProcessingProps,
  });
};

const refreshTrackingSuccess = (action$, wantedJobId, resolver) => {
  return action$.pipe(
    filter((action) => {
      return action.type === Actions.REFRESH_TRACKING_SUCCESS && action.jobGuid === wantedJobId;
    }),
    take(1),
    mergeMap((action) => {
      const jobGuid = get(action, 'jobGuid', '');
      const oldNotification = PROCESSING_NOTIFICATION_IDS[jobGuid];
      NotificationService.removeNotification(oldNotification);
      delete PROCESSING_NOTIFICATION_IDS[jobGuid];

      const refreshSuccessProps = {
        title: <FormattedMessage {...messages.refreshSuccessTitle} />,
        content: <FormattedMessage {...messages.refreshSuccessContent} />,
        notificationType: 'info',
        duration: 2000,
      };
      NotificationService.showNotification({ ...refreshSuccessProps });
      return of(resolver());
    }),
  );
};

const refreshTrackingFailure = () => {
  const refreshFailureProps = {
    title: <FormattedMessage {...messages.refreshFailureTitle} />,
    content: <FormattedMessage {...messages.refreshFailureContent} />,
    notificationType: NOTIFICATION_TYPE_ERROR,
    duration: 2000,
  };
  NotificationService.showNotification({ ...refreshFailureProps });
  return EMPTY;
};

export const refreshTrackingFromReturnGridEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(ReturnGridActions.RETURNS_GRID_REFRESH_TRACKING),
    mergeMap(() => {
      const state = getState();
      const selectedReturnIds = Global.selectSelectedReturnsSelectedReturnIds(state);
      const allTheReturns = Global.selectReturnsGridReturns(state);
      const returns = allTheReturns.filter((r) => selectedReturnIds.indexOf(r.fulfillmentId) > -1).map((r) => r.fulfillmentId);
      return Api.refreshTracking({ fulfillmentIds: returns }).pipe(
        mergeMap((response) => {
          const jobGuids = get(response, 'jobGuids', emptyArray);
          const jobs = jobGuids.map((job) => {
            showProcessingNotification(job);
            return refreshTrackingSuccess(action$, job, ReturnActions.getReturnsSilently);
          });
          return merge(...jobs);
        }),
        catchError(refreshTrackingFailure),
      );
    }),
  );
};

export const refreshTrackingFromShipmentsGridEpic = (action$) => {
  return action$.pipe(
    ofType(ShipmentGridHeaderActions.SHIPMENTS_GRID_REFRESH_TRACKING),
    mergeMap((action) => {
      const { fulfillmentIds } = action;
      return Api.refreshTracking({ fulfillmentIds }).pipe(
        mergeMap((response) => {
          const jobGuids = get(response, 'jobGuids', emptyArray);
          const jobs = jobGuids.map((job) => {
            showProcessingNotification(job);
            return refreshTrackingSuccess(action$, job, ShipmentActions.silentRequestGetShipments);
          });
          return merge(...jobs);
        }),
        catchError(refreshTrackingFailure),
      );
    }),
  );
};
